const DEFAULT_CONCURRENCY = 4
const DEFAULT_MAX_PAGES = 50

// Anything without a case-insensitive get() counts as no pagination advertise
const readTotalPages = response => {
  const headers = response?.headers
  if (!headers || typeof headers.get !== 'function') return null

  const raw = headers.get('total-pages')
  if (raw === null || raw === undefined || raw === '') return null

  const totalPages = Number(raw)
  return Number.isFinite(totalPages) ? totalPages : null
}

const isRecordList = response => Array.isArray(response?.data)

// Raw id, no coercion: product rows carry string sentinel ids ('-4') that
// String(id) would collapse against the integer -4.
const dedupeById = records => {
  const seen = new Set()

  return records.filter(record => {
    const id = record?.id
    if (id === undefined || id === null) return true
    if (seen.has(id)) return false
    seen.add(id)
    return true
  })
}

const describeEndpoint = (label, firstResponse) =>
  label || firstResponse?.config?.url || 'an unknown endpoint'

export const withPage = (url, page) =>
  page === 1 ? url : `${url}${url.includes('?') ? '&' : '?'}page=${page}`

export const fetchAllPages = async (fetchPage, opts = {}) => {
  const {
    concurrency = DEFAULT_CONCURRENCY,
    maxPages = DEFAULT_MAX_PAGES,
    label,
  } = opts

  const firstResponse = await fetchPage(1)

  const totalPages = readTotalPages(firstResponse)
  if (totalPages === null || totalPages <= 1) return firstResponse

  const lastPage = Math.min(totalPages, maxPages)
  if (lastPage <= 1) return firstResponse

  if (!isRecordList(firstResponse)) {
    console.error(
      `fetchAllPages: ${describeEndpoint(
        label,
        firstResponse
      )} advertises ${totalPages} total-pages but page 1 is not a list; returning the first page unpaged rather than discarding its payload.`
    )
    return firstResponse
  }

  const pages = []
  pages[1] = firstResponse.data

  let nextPage = 2
  const worker = async () => {
    while (nextPage <= lastPage) {
      const page = nextPage
      nextPage += 1

      try {
        const response = await fetchPage(page)

        if (!isRecordList(response)) {
          console.error(
            `fetchAllPages: page ${page} of ${describeEndpoint(
              label,
              firstResponse
            )} is not a list; skipping it, so the result is incomplete.`
          )
          continue
        }

        pages[page] = response.data
      } catch (error) {
        console.error(
          `fetchAllPages: page ${page} of ${describeEndpoint(
            label,
            firstResponse
          )} failed; continuing with the pages already collected.`,
          error
        )
      }
    }
  }

  const workerCount = Math.max(1, Math.min(concurrency, lastPage - 1))
  await Promise.allSettled(Array.from({ length: workerCount }, () => worker()))

  if (totalPages > maxPages) {
    console.error(
      `fetchAllPages: ${describeEndpoint(
        label,
        firstResponse
      )} advertises ${totalPages} total-pages, above the ${maxPages}-request cap; fetched ${lastPage} pages, so the result is incomplete.`
    )
  }

  const merged = []
  for (let page = 1; page <= lastPage; page += 1) {
    if (pages[page]) merged.push(...pages[page])
  }

  return { ...firstResponse, data: dedupeById(merged) }
}
